/* eslint-disable no-shadow */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import BottomBar from '../BottomBar';
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';
import GeneralAvailability from '../form/GeneralAvailability';
import { modalities } from '../helpers';
import Checkbox from '../form/Checkbox';
import usePersistedDraft from '../hooks/usePersistedDraft';
import {
  updateGeneralAvailability,
  updateCounsellingPreference,
  updateAgeGroups,
} from '../../../actions/preferences';

const PracticeDetails2 = ({
  ageGroups,
  openLoadingPanel,
  updateCounsellingPreference,
  updateGeneralAvailability,
  updateAgeGroups,
  closeLoadingPanel,
  nextPage,
  previousPage,
  errorCallback,
  currentSubStep,
  intl,
  draftValues,
  onDraftChange,
}) => {
  const { control, handleSubmit, getValues } = useForm({
    defaultValues: draftValues || {
      general_availability: [],
      modalities: [],
      age_groups: [],
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const onSubmit = async values => {
    const errorMessage = (
      <FormattedMessage defaultMessage="There was a problem updating your practice details." />
    );

    const counsellingPreferenceValues = {
      counselling_preference: {
        modalities: values.modalities,
      },
    };

    const generalAvailabilityValues = {
      general_availability: {
        weekday_day_yn: values.general_availability.includes('weekdays_day'),
        weekday_night_yn:
          values.general_availability.includes('weekdays_night'),
        weekend_day_yn: values.general_availability.includes('weekends_day'),
        weekend_night_yn:
          values.general_availability.includes('weekends_night'),
      },
    };

    const ageGroupValues = {
      age_groups: values.age_groups,
    };

    openLoadingPanel(
      <FormattedMessage defaultMessage="Updating practice details..." />,
    );

    try {
      await Promise.all([
        updateCounsellingPreference(counsellingPreferenceValues),
        updateGeneralAvailability(generalAvailabilityValues),
        updateAgeGroups(ageGroupValues),
      ]);
      closeLoadingPanel();
      nextPage();
    } catch (error) {
      errorCallback(error, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>
          <FormattedMessage defaultMessage="Practice Details" />
        </h1>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <div className="sub-page-indicator">{`${currentSubStep + 1}/7`}</div>
      </div>

      <h3 style={{ marginBottom: '25px' }}>
        <FormattedMessage defaultMessage="What is your general availability to see clients?" />
      </h3>
      <div className="input-row">
        <Controller
          name="general_availability"
          control={control}
          rules={{
            validate: value =>
              value && value.length > 0
                ? true
                : intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState }) => (
            <GeneralAvailability
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      <h3>
        <FormattedMessage defaultMessage="Which modalities would you like to use to take care of clients? (check all that apply)" />
      </h3>
      <div className="input-row">
        <Controller
          name="modalities"
          control={control}
          rules={{
            validate: value =>
              value && value.length > 0
                ? true
                : intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState }) => (
            <Checkbox
              defaultValue={field.value}
              onChange={field.onChange}
              options={modalities}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      <h3 style={{ marginTop: 20, marginBottom: 10 }}>
        <FormattedMessage defaultMessage="What age group(s) do you feel comfortable counselling?" />
      </h3>
      <div className="input-row">
        <Controller
          name="age_groups"
          control={control}
          rules={{
            validate: value =>
              value && value.length > 0
                ? true
                : intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState }) => (
            <MultiSelectCheckbox
              value={field.value}
              onChange={field.onChange}
              options={ageGroups}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      <BottomBar
        openLoadingPanel={openLoadingPanel}
        closeLoadingPanel={closeLoadingPanel}
        nextPage={nextPage}
        errorCallback={errorCallback}
        currentSubStep={currentSubStep}
        previousPage={previousPage}
      />
    </form>
  );
};

PracticeDetails2.propTypes = {
  ageGroups: PropTypes.array.isRequired,
  openLoadingPanel: PropTypes.func.isRequired,
  updateCounsellingPreference: PropTypes.func.isRequired,
  updateGeneralAvailability: PropTypes.func.isRequired,
  updateAgeGroups: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  currentSubStep: PropTypes.number.isRequired,
  intl: PropTypes.object.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
  previousPage: PropTypes.func,
};

export default connect(null, {
  updateCounsellingPreference,
  updateGeneralAvailability,
  updateAgeGroups,
})(injectIntl(PracticeDetails2));
